import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MoonMark from "@/components/MoonMark";
import AdminInsights from "@/components/AdminInsights";
import AdminPlaylist from "@/components/AdminPlaylist";
import AdminPricing from "@/components/AdminPricing";
import AdminVideos from "@/components/AdminVideos";
import AdminDonations from "@/components/AdminDonations";
import AdminTestimonials from "@/components/AdminTestimonials";
import AdminContent from "@/components/AdminContent";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
  window.location.origin + "/admin"
)}`;

const ALLOWED_EMAIL = "paulelliottmessier@gmail.com";

const GoogleGlyph = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6Z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.9-5l-3.9 3C3.2 21.3 7.3 24 12 24Z" />
    <path fill="#FBBC05" d="M5.1 14.3c-.3-.8-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.4 8.3 0 10.1 0 12s.4 3.7 1.2 5.3l3.9-3Z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l2.7-2.6C15.9 1.1 13.2 0 12 0 7.3 0 3.2 2.7 1.2 6.7l3.9 3c1-2.9 3.7-5 6.9-5Z" />
  </svg>
);

const Admin = () => {
  const location = useLocation();
  const [identity, setIdentity] = useState("checking");
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("nox_admin_token") || "");
  const [passphrase, setPassphrase] = useState("");
  const [messages, setMessages] = useState([]);
  const [replies, setReplies] = useState({});
  const [error, setError] = useState("");
  const [authError, setAuthError] = useState(location.state?.authError || "");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("inbox");

  const STATUS_META = {
    pending: { label: "Pending", cls: "border-line text-slate-400" },
    in_progress: { label: "In the Forge", cls: "border-gold/60 text-gold" },
    fulfilled: { label: "Fulfilled", cls: "border-emerald-500/60 text-emerald-400" },
  };

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/messages`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (res.status === 401) {
        setIdentity("none");
        setUser(null);
        return;
      }
      if (!res.ok) throw new Error("failed");
      setMessages(await res.json());
    } catch {
      setError("Could not load messages. Is the backend awake?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const passed = location.state?.user;
      if (passed) {
        setUser(passed);
        setIdentity("google");
        load("");
        return;
      }
      const t = localStorage.getItem("nox_admin_token");
      if (t) {
        setToken(t);
        setIdentity("jwt");
        load(t);
        return;
      }
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const u = await res.json();
        if (u.email !== ALLOWED_EMAIL) throw new Error();
        setUser(u);
        setIdentity("google");
        load("");
      } catch {
        setIdentity("none");
      }
    };
    init();
  }, []); // eslint-disable-line

  const signInWithGoogle = () => {
    window.location.href = GOOGLE_AUTH_URL;
  };

  const sendReply = async (id) => {
    const text = (replies[id] || "").trim();
    if (!text) return;
    await fetch(`${API}/api/admin/messages/${id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ text }),
    });
    setReplies({ ...replies, [id]: "" });
    load(token || "");
  };

  const setMsgStatus = async (id, status) => {
    await fetch(`${API}/api/admin/messages/${id}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    load(token || "");
  };

  const issueReceipt = async (id) => {
    await fetch(`${API}/api/admin/messages/${id}/receipt`, {
      method: "POST",
      credentials: "include",
    });
    load(token || "");
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Login failed");
        return;
      }
      localStorage.setItem("nox_admin_token", data.token);
      setToken(data.token);
      setIdentity("jwt");
      load(data.token);
    } catch {
      setError("Could not reach the backend.");
    }
  };

  const leave = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      // cookie may not exist; proceed
    }
    localStorage.removeItem("nox_admin_token");
    setToken("");
    setUser(null);
    setMessages([]);
    setIdentity("none");
  };

  if (identity === "checking") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink">
        <MoonMark className="h-12 w-12 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <p className="font-jmono text-[10px] uppercase tracking-[0.4em] text-slate-500">
          Checking the seals…
        </p>
      </main>
    );
  }

  if (identity !== "google" && identity !== "jwt") {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md border border-line bg-surface p-8" data-testid="admin-login">
          <MoonMark className="mx-auto h-10 w-10 text-gold" />
          <h1 className="mt-6 text-center font-display text-2xl tracking-[0.2em] text-silver">
            THE NIGHT VAULT
          </h1>
          <p className="mt-2 text-center text-xs text-slate-500">
            Only the keeper may read the inbox.
          </p>
          <button
            onClick={signInWithGoogle}
            data-testid="admin-google-signin"
            className="mt-8 flex w-full cursor-pointer items-center justify-center gap-3 rounded-full border border-slate-600/50 bg-[#0d0d12] px-6 py-3 text-sm text-silver transition-all duration-300 hover:border-slate-400 hover:bg-[#14141c]"
          >
            <GoogleGlyph />
            Sign in with Google
          </button>
          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-line" />
            <span className="font-jmono text-[9px] uppercase tracking-[0.35em] text-slate-600">or</span>
            <span className="h-px flex-1 bg-line" />
          </div>
          <form onSubmit={login}>
            <input
              className="input-night"
              type="password"
              placeholder="Backup passphrase"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              data-testid="admin-passphrase"
            />
            {(error || authError) && (
              <p className="mt-3 text-xs text-red-500" data-testid="admin-auth-error">
                {error || authError}
              </p>
            )}
            <button className="btn-night mt-6 w-full" type="submit" data-testid="admin-login-submit">
              Enter
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-6 pb-24 pt-32 lg:px-10" data-testid="admin-inbox">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || "Keeper"}
              data-testid="admin-avatar"
              className="h-10 w-10 rounded-full border border-gold/40 object-cover"
            />
          ) : (
            <MoonMark className="h-10 w-10 text-gold" />
          )}
          <div>
            <h1 className="font-display text-3xl tracking-[0.15em] text-silver">INBOX</h1>
            {user?.name && (
              <p className="mt-1 font-jmono text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="admin-user-name">
                {user.name}
              </p>
            )}
          </div>
        </div>
        <button onClick={leave} className="btn-ghost-night !px-5 !py-2" data-testid="admin-logout">
          Leave
        </button>
      </div>
      <div className="mt-6 flex items-center gap-3" data-testid="admin-tabs">
        <button
          onClick={() => setTab("inbox")}
          data-testid="admin-tab-inbox"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "inbox" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Inbox
        </button>
        <button
          onClick={() => setTab("insights")}
          data-testid="admin-tab-insights"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "insights" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Insights
        </button>
        <button
          onClick={() => setTab("pricing")}
          data-testid="admin-tab-pricing"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "pricing" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Pricing
        </button>
        <button
          onClick={() => setTab("videos")}
          data-testid="admin-tab-videos"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "videos" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Videos
        </button>
        <button
          onClick={() => setTab("offerings")}
          data-testid="admin-tab-offerings"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "offerings" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Offerings
        </button>
        <button
          onClick={() => setTab("testimonials")}
          data-testid="admin-tab-testimonials"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "testimonials" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Testimonials
        </button>
        <button
          onClick={() => setTab("content")}
          data-testid="admin-tab-content"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "content" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Content
        </button>
        <button
          onClick={() => setTab("playlist")}
          data-testid="admin-tab-playlist"
          className={`cursor-pointer border px-4 py-2 font-jmono text-[10px] uppercase tracking-[0.3em] transition-colors duration-300 ${
            tab === "playlist" ? "border-gold/60 text-gold" : "border-line text-slate-500 hover:text-silver"
          }`}
        >
          Playlist
        </button>
      </div>

      {tab === "inbox" && (
        <p className="mt-2 font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500" data-testid="admin-count">
          {loading ? "Summoning…" : `${messages.length} message${messages.length === 1 ? "" : "s"}`}
        </p>
      )}

      {tab === "insights" && <AdminInsights />}

      {tab === "pricing" && <AdminPricing />}

      {tab === "videos" && <AdminVideos />}

      {tab === "offerings" && <AdminDonations />}

      {tab === "testimonials" && <AdminTestimonials />}

      {tab === "content" && <AdminContent />}

      {tab === "playlist" && <AdminPlaylist />}

      <div className={`mt-10 space-y-6 ${tab !== "inbox" ? "hidden" : ""}`}>
        {messages.map((m) => (
          <article key={m.id} className="border border-line bg-surface p-6" data-testid="admin-message">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-display text-lg text-silver">
                {m.name} <span className="text-sm text-slate-500">· {m.email}</span>
              </p>
              <p className="font-jmono text-[10px] tracking-[0.2em] text-slate-600">
                {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
              </p>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm font-light leading-relaxed text-slate-300">
              {m.message}
            </p>
            {m.quote && (
              <div className="mt-4 border border-gold/30 bg-[#0d0d10] px-4 py-3" data-testid="admin-message-quote">
                <p className="font-jmono text-[10px] uppercase tracking-[0.3em] text-gold">
                  Attached quote · {m.quote.estimate ? `$${m.quote.estimate.toLocaleString()} USD` : ""}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {[m.quote.service, m.quote.scale, m.quote.deadline, m.quote.revisions, m.quote.licensing, m.quote.rush ? "Rush" : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3" data-testid={`admin-tools-${m.id}`}>
              <Select value={m.status || "pending"} onValueChange={(v) => setMsgStatus(m.id, v)}>
                <SelectTrigger className="!h-8 w-44 border-line bg-[#0d0d12] text-xs text-silver" data-testid={`admin-status-select-${m.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-line bg-[#0d0d12]">
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span
                className={`border px-3 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] ${
                  (STATUS_META[m.status || "pending"] || STATUS_META.pending).cls
                }`}
                data-testid={`admin-status-badge-${m.id}`}
              >
                {(STATUS_META[m.status || "pending"] || STATUS_META.pending).label}
              </span>
              {m.receipt ? (
                <Link
                  to={`/receipt/${m.id}`}
                  data-testid={`admin-receipt-link-${m.id}`}
                  className="border border-emerald-500/50 px-3 py-1.5 font-jmono text-[9px] uppercase tracking-[0.25em] text-emerald-400 transition-colors hover:bg-emerald-500/10"
                >
                  Receipt {m.receipt.number}
                </Link>
              ) : (
                <button
                  onClick={() => issueReceipt(m.id)}
                  data-testid={`admin-receipt-issue-${m.id}`}
                  className="btn-ghost-night cursor-pointer !px-4 !py-1.5 !text-[9px]"
                >
                  Issue Receipt
                </button>
              )}
            </div>

            {(m.replies || []).length > 0 && (
              <div className="mt-5 space-y-3 border-l-2 border-gold/40 pl-4" data-testid={`admin-thread-${m.id}`}>
                {m.replies.map((r, j) => (
                  <div key={j}>
                    <p className="font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/80">
                      {r.from === "nox" ? "Nox Harmonia" : r.name || "Visitor"} ·{" "}
                      <span className="text-slate-600">{r.at ? new Date(r.at).toLocaleString() : ""}</span>
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-slate-300">
                      {r.text}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 flex gap-3">
              <textarea
                className="input-night min-h-[60px] flex-1 resize-y"
                placeholder="Reply to the visitor — they see it under My Quotes…"
                value={replies[m.id] || ""}
                onChange={(e) => setReplies({ ...replies, [m.id]: e.target.value })}
                data-testid={`admin-reply-textarea-${m.id}`}
              />
              <button
                className="btn-ghost-night self-end !px-5 !py-2"
                onClick={() => sendReply(m.id)}
                data-testid={`admin-reply-btn-${m.id}`}
              >
                Reply
              </button>
            </div>
          </article>
        ))}
        {!loading && messages.length === 0 && (
          <p className="py-20 text-center font-cormorant text-lg italic text-slate-600">
            Silence. No messages yet.
          </p>
        )}
      </div>
    </main>
  );
};

export default Admin;
